import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {BreadcrumbName} from 'common/src/modules/breadcrumbs/breadcrumbs.enums';
import {MoviesObj} from 'common/src/modules/movieEditor/movieEditor.types';
import {Format} from '../../../creativeTemplates.types';
import {FormatMoviesState, FormatPreviewPosition} from './formatMovies.types';

export const formatMoviesInitialState: FormatMoviesState = {
  activeCompId: '',
  activeLayerIds: [],
  movies: {},
  previewPrecompId: null,
  isScaleLinked: true,
  movieColors: [],
  initialState: {},
  isMoviesLoading: true,
  isKeyframesGraphOn: false,
  formatsChanged: [],
  isNewImageEditorOpen: false,
  multiSelectedFormats: [],
  formatPreviewPositions: {},
  isMultiFormatView: false,
};

const formatMoviesReducer = createSlice({
  name: BreadcrumbName.AdBuilder,
  initialState: formatMoviesInitialState,
  reducers: {
    resetState: () => formatMoviesInitialState,
    setInitialState: (state, action: PayloadAction<MoviesObj>) => {
      state.initialState = action.payload;
    },
    setFormatMoviesData: (
      state,
      action: PayloadAction<{
        movies: MoviesObj;
        activeCompId: string;
        activeLayerIds: string[];
        initialState: MoviesObj;
        isMoviesLoading: boolean;
      }>,
    ) => {
      state.movies = action.payload.movies;
      state.activeCompId = action.payload.activeCompId;
      state.activeLayerIds = action.payload.activeLayerIds;
      state.previewPrecompId = null;
      state.initialState = action.payload.initialState;
      state.isMoviesLoading = action.payload.isMoviesLoading;
    },

    updateMovies: (state, action: PayloadAction<MoviesObj>) => {
      state.movies = action.payload;
    },

    setActiveComp: (state, action: PayloadAction<string>) => {
      state.activeCompId = action.payload;
    },

    setPreviewPrecomp: (state, action: PayloadAction<string | null>) => {
      state.previewPrecompId = action.payload;
    },

    clearPreviewPrecomp: (state) => {
      state.previewPrecompId = null;
    },

    setActiveLayerIds: (state, action: PayloadAction<string[]>) => {
      state.activeLayerIds = action.payload;
    },

    setIsScaleLinked: (state, action: PayloadAction<boolean>) => {
      state.isScaleLinked = action.payload;
    },

    setMovieColors: (state, action: PayloadAction<string[]>) => {
      state.movieColors = action.payload;
    },

    setIsKeyframesGraphOn: (state, action: PayloadAction<boolean>) => {
      state.isKeyframesGraphOn = action.payload;
    },

    setFormatsChanged: (state, action: PayloadAction<string[]>) => {
      state.formatsChanged = action.payload;
    },

    setIsNewImageEditorOpen: (state, action: PayloadAction<boolean>) => {
      state.isNewImageEditorOpen = action.payload;
    },

    setMultiSelectedFormats: (state, action: PayloadAction<string[]>) => {
      state.multiSelectedFormats = action.payload;
    },

    toggleMultiSelectedFormat: (state, action: PayloadAction<string>) => {
      const index = state.multiSelectedFormats.indexOf(action.payload);

      if (index === -1) {
        state.multiSelectedFormats.push(action.payload);
      } else {
        state.multiSelectedFormats.splice(index, 1);
      }
    },

    setFormatPreviewPosition: (
      state,
      action: PayloadAction<{formatString: string; position: FormatPreviewPosition}>,
    ) => {
      state.formatPreviewPositions[action.payload.formatString] = action.payload.position;
    },

    setFormatPreviewPositionsFromTemplate: (state, action: PayloadAction<Format[]>) => {
      state.formatPreviewPositions = {};

      action.payload.forEach((format) => {
        if (format.previewPosition) {
          state.formatPreviewPositions[format.format.formatString] = format.previewPosition;
        }
      });
    },

    setIsMultiFormatView: (state, action: PayloadAction<boolean>) => {
      state.isMultiFormatView = action.payload;
    },
  },
});

export const {
  resetState,
  setFormatMoviesData,
  updateMovies,
  setActiveComp,
  setPreviewPrecomp,
  clearPreviewPrecomp,
  setActiveLayerIds,
  setIsScaleLinked,
  setMovieColors,
  setIsKeyframesGraphOn,
  setFormatsChanged,
  setIsNewImageEditorOpen,
  setInitialState,
  setMultiSelectedFormats,
  toggleMultiSelectedFormat,
  setFormatPreviewPosition,
  setFormatPreviewPositionsFromTemplate,
  setIsMultiFormatView,
} = formatMoviesReducer.actions;

export default formatMoviesReducer.reducer;
